import { useMutation, useQueryClient } from "@tanstack/react-query";

import { guacHttpClient } from "@/lib/http";
import {
  CalendarAndTasksData,
  DashboardInterview,
  HttpResponse,
  Interview,
  InterviewStatus,
  UpdateInterviewRequest,
} from "@/types";

// Updates interview status from the shared 7-day schedule
export function useUpdateSetDashboardInterview() {
  const queryClient = useQueryClient();

  const mapStatus = <T extends { id: number; status: InterviewStatus | null }>(
    cachedData: T[] | undefined,
    id: number,
    status: InterviewStatus | null,
  ) =>
    cachedData?.map((interview) =>
      interview.id === id ? { ...interview, status } : interview,
    );

  return useMutation({
    mutationFn: async ({
      companyId,
      jobId,
      id,
      status,
    }: {
      companyId: string;
      jobId: string;
      id: number;
      status: InterviewStatus;
    }) => {
      const path = `companies/${companyId}/jobs/${jobId}/interviews/${id.toString()}`;
      const res = await guacHttpClient.patch<
        Interview,
        HttpResponse<Interview>,
        UpdateInterviewRequest
      >(path, { status });

      return res.data;
    },
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ["calendar-and-tasks"] });
      await queryClient.cancelQueries({ queryKey: ["interviews"] });
      await queryClient.cancelQueries({ queryKey: ["interviews-calendar"] });

      const previousCalendar = queryClient.getQueriesData<CalendarAndTasksData>(
        {
          queryKey: ["calendar-and-tasks"],
        },
      );
      const previousInterviews = queryClient.getQueriesData<Interview[]>({
        queryKey: ["interviews"],
      });
      const previousInterviewsCalendar = queryClient.getQueriesData<
        DashboardInterview[]
      >({ queryKey: ["interviews-calendar"] });

      // Optimistic cache updates — dashboard schedule (wrapped), job pipeline
      // schedule, and the job interviews table (both flat arrays).
      queryClient.setQueriesData<CalendarAndTasksData>(
        { queryKey: ["calendar-and-tasks"] },
        (cachedData) => {
          if (!cachedData) return cachedData;
          return {
            ...cachedData,
            interviews: mapStatus(cachedData.interviews, id, status) ?? [],
          };
        },
      );
      queryClient.setQueriesData<Interview[]>(
        { queryKey: ["interviews"] },
        (cachedData) => mapStatus(cachedData, id, status),
      );
      queryClient.setQueriesData<DashboardInterview[]>(
        { queryKey: ["interviews-calendar"] },
        (cachedData) => mapStatus(cachedData, id, status),
      );

      return {
        previousCalendar,
        previousInterviews,
        previousInterviewsCalendar,
      };
    },
    onError: (_err, _vars, context) => {
      context?.previousCalendar.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      context?.previousInterviews.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      context?.previousInterviewsCalendar.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (updatedInterview, { id }) => {
      queryClient.setQueriesData<CalendarAndTasksData>(
        { queryKey: ["calendar-and-tasks"] },
        (cachedData) => {
          if (!cachedData) return cachedData;
          return {
            ...cachedData,
            interviews:
              mapStatus(cachedData.interviews, id, updatedInterview.status) ??
              [],
          };
        },
      );
      queryClient.setQueriesData<Interview[]>(
        { queryKey: ["interviews"] },
        (cachedData) => mapStatus(cachedData, id, updatedInterview.status),
      );
      queryClient.setQueriesData<DashboardInterview[]>(
        { queryKey: ["interviews-calendar"] },
        (cachedData) => mapStatus(cachedData, id, updatedInterview.status),
      );
      // A status change can add or remove the interview from the in-flight
      // card's revenue-bearing set, so refetch rather than patch.
      void queryClient.invalidateQueries({ queryKey: ["staffer-in-flight"] });
    },
  });
}
